import { useCallback } from 'react';
import type { PlanKey } from '../plans';

// App Bridge 4 is loaded via CDN — types declared in src/shopify.d.ts.

/** Fired on window when the API says the merchant has no active subscription. */
export const BILLING_REQUIRED_EVENT = 'signshield:billing-required';

export interface BillingRequiredDetail {
  subscriptionStatus: string;
  /** One trial per store: 7 for new stores, less (or 0) after a previous subscription. */
  trialDaysRemaining: number;
}

/**
 * Returns `request`, `upload`, `getToken` and `startSubscription` helpers that
 * automatically attach a fresh App Bridge 4 session token (idToken) to every
 * API call.
 *
 * 402 handling: dispatches BILLING_REQUIRED_EVENT so the app shows the
 * welcome / start-trial screen. The Shopify charge is only created when the
 * merchant clicks the button there (startSubscription).
 */
export function useApi() {
  const getToken = useCallback(async (): Promise<string> => {
    return shopify.idToken();
  }, []);

  const handlePaymentRequired = useCallback((res: Response) => {
    res
      .json()
      .catch(() => ({}))
      .then((body: any) => {
        window.dispatchEvent(
          new CustomEvent<BillingRequiredDetail>(BILLING_REQUIRED_EVENT, {
            detail: {
              subscriptionStatus: body?.subscriptionStatus ?? 'NONE',
              trialDaysRemaining: body?.trialDaysRemaining ?? 0,
            },
          }),
        );
      });
  }, []);

  /**
   * Creates the Shopify subscription and navigates the admin to Shopify's
   * approval page. Reloads instead if the store turns out to be subscribed.
   */
  const startSubscription = useCallback(async (plan?: PlanKey) => {
    const token = await getToken();
    const res = await fetch(`/api/auth/billing-start${plan ? `?plan=${plan}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).message ?? `Could not start billing (HTTP ${res.status})`);
    }
    const { confirmationUrl, alreadySubscribed } = await res.json();
    if (alreadySubscribed) {
      window.location.reload();
    } else if (confirmationUrl) {
      // App Bridge 4 intercepts window.open(_top) and navigates the admin frame.
      window.open(confirmationUrl, '_top');
    } else {
      throw new Error('Could not start billing');
    }
  }, [getToken]);

  /** JSON API request — sets Content-Type and Authorization automatically. */
  const request = useCallback(
    async <T>(path: string, options: RequestInit = {}): Promise<T> => {
      const token = await getToken();
      const { headers: extraHeaders, ...rest } = options;
      const res = await fetch(`/api${path}`, {
        ...rest,
        headers: {
          'Content-Type': 'application/json',
          ...(extraHeaders as Record<string, string>),
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 402) {
        handlePaymentRequired(res);
        // The welcome screen replaces the page — return a pending promise so
        // the page shows no error while it is swapped out.
        return new Promise<T>(() => {});
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message ?? `HTTP ${res.status}`);
      }
      if (res.status === 204) return undefined as T;
      return res.json();
    },
    [getToken, handlePaymentRequired],
  );

  /** Multipart upload — do NOT set Content-Type (browser sets the boundary). */
  const upload = useCallback(
    async <T>(path: string, formData: FormData): Promise<T> => {
      const token = await getToken();
      const res = await fetch(`/api${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.status === 402) {
        handlePaymentRequired(res);
        return new Promise<T>(() => {});
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
    [getToken, handlePaymentRequired],
  );

  return { request, upload, getToken, startSubscription };
}
